import { Box, Typography, Card, Alert, Button, CircularProgress, IconButton } from '@mui/material'
import { Eye, Trash2 } from 'lucide-react'

import { PaginationFooter } from '../../../common/components'
import { MODULE_NAME, PERMISSIONS } from '../../../constants'
import { useLanguage } from '../../../i18n'
import { hasPermission } from '../../../utils'

function RoleCard({ role, onEdit, onDelete, onView, t }) {
  // Branch-scoped permissions — hide update/delete controls the current
  // user is not allowed to use.
  const canUpdate = hasPermission(MODULE_NAME.ROLE, PERMISSIONS.UPDATE)
  const canDelete = hasPermission(MODULE_NAME.ROLE, PERMISSIONS.DELETE)

  return (
    <Card className="p-5">
      <Box className="flex justify-between items-center mb-2">
        <Typography variant="body1" color="var(--text-secondary)">
          {t('Total users')}{' '}
          <Box component="span" sx={{ color: 'var(--text-primary)', fontWeight: 600, ml: 0.5 }}>
            {role.totalUsers ?? 0}
          </Box>
        </Typography>
        <Box className="flex items-center gap-0.5">
          <IconButton
            size="small"
            aria-label={t('View role')}
            onClick={() => onView && onView(role)}
          >
            <Eye size={16} />
          </IconButton>
          {canDelete && (
            <IconButton
              size="small"
              aria-label={t('Delete role')}
              onClick={() => onDelete && onDelete(role)}
            >
              <Trash2 size={16} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Typography component="h5" variant="h5" className="pb-3" fontWeight={700}>
        {role.name}
      </Typography>

      {canUpdate && (
        <Typography
          onClick={() => onEdit && onEdit(role)}
          sx={{ mt: 'auto', cursor: 'pointer' }}
          fontWeight={600}
          fontSize={13}
          color="var(--text-secondary)"
        >
          {t('Edit Role')}
        </Typography>
      )}
    </Card>
  )
}

function RolesTab({
  roles,
  onEdit,
  onDelete,
  onView,
  isLoading,
  isError,
  errorMessage,
  onRetry,
  // Pagination props are driven by the parent (UsersRoles) — the parent
  // owns the React Query that fetches the page slice, so this tab is a
  // pure renderer that mirrors UserTable's split. `<PaginationFooter>`
  // expects 0-indexed `page` + a `setPerPage` setter (so the rows-per-
  // page dropdown can reset the page back to 0 on change).
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) {
  // Subscribe to language changes so the cards re-render with the new
  // locale. Without this the rendered copy is frozen in the language
  // active at first mount and only updates after a page refresh.
  const { t } = useLanguage()

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  if (isError) {
    return (
      <Alert
        severity="error"
        sx={{ mb: 2 }}
        action={
          onRetry && (
            <Button color="inherit" size="small" onClick={onRetry}>
              {t('Retry')}
            </Button>
          )
        }
      >
        {errorMessage || t('Failed to load roles.')}
      </Alert>
    )
  }

  const roleList = Array.isArray(roles) ? roles : []
  const hasRoles = roleList.length > 0

  return (
    <>
      {hasRoles ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(3, 1fr)',
            },
            gap: 2.5,
          }}
        >
          {roleList.map((role) => (
            <RoleCard
              key={role.id}
              role={role}
              onEdit={onEdit}
              onDelete={onDelete}
              onView={onView}
              t={t}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ py: 8, textAlign: 'center', color: 'var(--text-secondary)' }}>
          <Typography variant="body1">{t('No roles yet. Create one to get started.')}</Typography>
        </Box>
      )}

      {/* Footer renders unconditionally — when there's exactly one page
          the Previous/Next/Go-to-page controls all read as disabled, so
          the user still sees the rows-per-page picker and can pre-size
          the layout before more roles land. Skipping the footer on a
          "no results" empty state was confusing because users landed on
          an unconfigurable grid. */}
      <PaginationFooter
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={onPageChange}
        setPerPage={onRowsPerPageChange}
      />
    </>
  )
}

export default RolesTab
